import { useEffect, useState } from "react";
import { Navigate, Outlet, RouteObject, useParams } from "react-router-dom";
import { Skeleton, Button, darken, CircularProgress, Stack } from "@mui/material";
import CollaboratorsModal from "datasets/components/CollaboratorsModal";
import RequestAccessModal from "datasets/components/RequestAccessModal";
import useDatasetAssistant from "datasets/components/assistant/hooks/useDatasetAssistant";
import useDataset from "datasets/hooks/useDataset";
import useIsMasterDataset from "datasets/hooks/useIsMasterDataset";
import DatasetAnalysis from "datasets/pages/analysis/DatasetAnalysis";
import DatasetReport from "datasets/pages/dashboards/pages/DatasetReport";
import DatasetReports from "datasets/pages/dashboards/pages/DatasetReports";
import DatasetFiles from "datasets/pages/files/DatasetFiles";
import DatasetOverview from "datasets/pages/overview/DatasetOverview";
import DatasetSettings from "datasets/pages/settings/DatasetSettings";
import DatasetTables from "datasets/pages/tables/DatasetTables";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import yn from "yn";
import { KeyOutlinedIcon } from "~/assets/icons";
import { TitleBreadcrumbs } from "~/components/AppHeader";
import { ProtectedAppRoute } from "~/components/ProtectedAppRoute";
import RouteTabs, { RouteTab } from "~/components/RouteTabs";
import useAppHeader from "~/contexts/header";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import useUserPermission from "~/hooks/useUserPermission";
import { ROUTE_404 } from "~/router/constants";
import { trackEvent } from "~/utils/monitoring";

const { VITE_DATASETS_ENABLED, VITE_DATASET_FILES_ENABLED, VITE_DATASET_PERMISSIONS_ENABLED } =
  import.meta.env;

// Create a wrapper component for the analysis route until we have a proper permission defined
const AnalysisRoute = () => {
  const assistantEnabled = useDatasetAssistant();
  return assistantEnabled ? <DatasetAnalysis /> : null;
};

const routeDefinitions: RouteObject[] = [
  { index: true, element: <Navigate to="overview" replace /> },
  { path: "overview", element: <DatasetOverview /> },
  {
    path: "reports",
    element: (
      <ProtectedAppRoute permission="list:catalog-report">
        <DatasetReports />
      </ProtectedAppRoute>
    ),
  },
  {
    path: "analysis",
    element: <AnalysisRoute />,
  },
  {
    path: "reports/:reportId",
    element: (
      <ProtectedAppRoute permission="list:catalog-report">
        <DatasetReport />
      </ProtectedAppRoute>
    ),
  },
  {
    path: "tables/*",
    element: (
      <ProtectedAppRoute permission="list:catalog-table">
        <DatasetTables />
      </ProtectedAppRoute>
    ),
  },
  {
    path: "files/:prefixId?/*",
    element: (
      <ProtectedAppRoute permission="list:catalog-file">
        <DatasetFiles />
      </ProtectedAppRoute>
    ),
  },

  {
    path: "settings/*",
    element: <DatasetSettings />,
  },
];

const DatasetDetails = () => {
  const user = useUser();
  const permissions = user?.permissions ?? [];
  const { datasetId, "*": subRoute } = useParams<{ datasetId: string; "*": string }>();
  const routes = useSafeRoutes(routeDefinitions);
  const { data: dataset, isLoading, error } = useDataset(datasetId);
  const isMasterDataset = useIsMasterDataset();
  const hasEditPermission = useUserPermission("edit:catalog-dataset");
  const showSettings = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.OWNER
    : hasEditPermission;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRequestAccessModalOpen, setIsRequestAccessModalOpen] = useState(false);
  const showAssistant = useDatasetAssistant();

  const currentTab = subRoute?.split("/")[0] || "overview";
  const isReportEmbed = currentTab === "reports" && subRoute?.split("/")[1];

  useDocumentTitle(dataset?.title);
  const { setTitle } = useAppHeader();

  // Track dataset views only if dataset or tab has changed
  useEffect(() => {
    if (dataset?.id && currentTab) {
      trackEvent("dataset_viewed", {
        dataset_id: dataset.id,
        page: currentTab === "reports" ? "dashboards" : currentTab,
      });
    }
  }, [dataset?.id, currentTab]);

  useEffect(() => {
    setTitle(
      <TitleBreadcrumbs
        crumbs={[
          { title: "Data Browser", href: "/data-browser" },
          { title: "Datasets", href: "/data-browser/datasets" },
          { title: isLoading ? <Skeleton width={75} /> : dataset?.title, href: location.pathname },
        ]}
      />
    );
  }, [dataset, isLoading, setTitle]);

  // Sanitize tab value to prevent MUI TabContext errors
  const getValidTabValue = () => {
    if (isLoading && currentTab === "settings") {
      return "overview";
    }
    if (!showAssistant && currentTab === "analysis") {
      return "overview";
    }
    return currentTab;
  };

  if (!yn(VITE_DATASETS_ENABLED) || error?.status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (!showSettings && currentTab === "settings" && !isLoading) {
    return <Navigate to="/404" replace />;
  }

  if (!showAssistant && currentTab === "analysis" && !isLoading) {
    return <Navigate to="overview" replace />;
  }

  // Build tabs based on permissions and feature flags
  const tabs: RouteTab[] = [
    { label: "Overview", value: "overview" },
    { label: "AI Analysis", value: "analysis", visible: showAssistant },
    { label: "Dashboards", value: "reports", visible: permissions.includes("list:catalog-report") },
    { label: "Tables", value: "tables", visible: permissions.includes("list:catalog-table") },
    {
      label: "Files",
      value: "files",
      visible:
        yn(VITE_DATASET_FILES_ENABLED) &&
        permissions.includes("list:catalog-file") &&
        !isMasterDataset,
    },
    { label: "Settings", value: "settings", visible: showSettings },
  ];

  // Action buttons for the tab bar
  const renderActions = () => {
    if (!yn(VITE_DATASET_PERMISSIONS_ENABLED)) {
      return null;
    }

    if (dataset?.access_level === DatasetAccessLevel.OWNER) {
      return (
        <Button
          data-testid="share-dataset-button"
          variant="contained"
          color="primary"
          onClick={() => setIsShareModalOpen(true)}
          sx={{ lineHeight: "16px", px: 1.5, py: 1 }}
        >
          Share
        </Button>
      );
    }

    if (dataset?.access_level === DatasetAccessLevel.PREVIEW) {
      return (
        <Button
          data-testid="request-dataset-access-button"
          variant="contained"
          color="secondary"
          onClick={() => setIsRequestAccessModalOpen(true)}
          sx={{
            lineHeight: "16px",
            px: 1.5,
            py: 1,
            backgroundColor: "#EEEEEE",
            "&:hover": {
              backgroundColor: darken("#EEEEEE", 0.1),
            },
          }}
          startIcon={<KeyOutlinedIcon width={20} />}
        >
          Upgrade Access
        </Button>
      );
    }

    return null;
  };

  // Loading state
  const loadingContent = (
    <Stack
      height="100%"
      flex={1}
      alignItems="center"
      justifyContent="center"
      bgcolor="common.pageBackground"
    >
      <CircularProgress />
    </Stack>
  );

  return (
    <>
      <RouteTabs
        tabs={tabs}
        currentTab={getValidTabValue()}
        actions={renderActions()}
        showTabs={!isReportEmbed}
      >
        {isLoading ? (
          loadingContent
        ) : (
          <>
            {routes}
            <Outlet />
          </>
        )}
      </RouteTabs>

      {isShareModalOpen && (
        <CollaboratorsModal
          open={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          datasetId={datasetId}
        />
      )}
      {isRequestAccessModalOpen && (
        <RequestAccessModal
          open={isRequestAccessModalOpen}
          onClose={() => setIsRequestAccessModalOpen(false)}
        />
      )}
    </>
  );
};

export default DatasetDetails;
